import type { RoiId } from '../../changes/ids';
import { SubstitutionLog } from '../../dicom/engine/charset';
import { domainTrace } from '../../diagnostics/domainTrace';
import { StaleReferenceError } from '../../errors/DomainError';
import { Patient } from '../../patient/patient';
import { StructureSet } from '../../patient/structureset';

export interface StructureSetExportOptions {
    /**
     * When provided, only these structures are included in the export
     * (delegates to {@link StructureSet.export}). When omitted, the
     * whole structure set is exported.
     */
    selectedRoiIds?: RoiId[];
    /** Optional DICOM character set override (e.g. 'ISO_IR 100'). */
    characterSet?: string;
    /**
     * When true, export as a Monaco DIR file rather than DICOM RTSTRUCT.
     * Requires the SS to be bound to a CT/MR image.
     */
    asMonacoFile?: boolean;
    /** Patient name embedded in the Monaco file. Used only when asMonacoFile. */
    patientName?: string;
    /**
     * Collects any text the chosen character set could not represent, so the
     * caller can tell the user what the receiving system will hold. Omit it
     * when exporting in a set that represents everything.
     */
    substitutions?: SubstitutionLog;
    /**
     * Write patient identity narrowed rather than refusing, once the user has
     * been shown what would change and has accepted it.
     */
    allowIdentityNarrowing?: boolean;
}

/**
 * Read-only domain service that produces export blobs from a structure
 * set without exposing the live {@link StructureSet} entity to UI code.
 *
 * The service does NOT call `environment.saveChanges()` — syncing
 * engine contour state into the domain is an engine/UI concern. Callers
 * must save engine changes before invoking the service.
 */
export class StructureSetExportService {
    constructor(private readonly patient: Patient) {}

    /**
     * Produce an export blob for the structure set with the given SOP
     * Instance UID.
     *
     * @throws {StaleReferenceError} if no SS with that UID exists.
     */
    exportBlob(
        structureSetUid: string,
        options: StructureSetExportOptions = {}
    ): Blob {
        domainTrace.service('StructureSetExportService.exportBlob', {
            structureSetUid,
            roiSubsetCount: options.selectedRoiIds?.length,
            asMonacoFile: options.asMonacoFile ?? false,
        });
        const ss = this.resolve(structureSetUid);
        if (options.asMonacoFile) {
            return ss.exportAsMonacoFile(options.patientName ?? 'NO_NAME');
        }
        // One call whether or not a subset of ROIs was named. Routing the two
        // separately is how a requested character set came to be honoured only
        // alongside a subset, so an export for a system that requires one got
        // it only when part of the set happened to be selected.
        return ss.export({
            roiIds: options.selectedRoiIds,
            characterSet: options.characterSet,
            log: options.substitutions,
            policy: {
                allowIdentityNarrowing: options.allowIdentityNarrowing,
            },
        });
    }

    private resolve(structureSetUid: string): StructureSet {
        const ss =
            this.patient.getStructureSetWithSopInstanceUid(structureSetUid);
        if (!ss) {
            throw new StaleReferenceError(
                `StructureSet ${structureSetUid} no longer exists.`
            );
        }
        return ss;
    }
}
